import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, 
  Download,
  Edit,
  Printer,
  Phone
} from 'lucide-react';
import LoadingSpinner, { ErrorMessage } from '../components/LoadingSpinner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { generateManagementFormPDF } from '../utils/generateManagementFormPDF';

const ManagementFormDetailPage = ({ formId, onBack }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const { toast } = useToast();
  const { practice } = useAuth();

  useEffect(() => {
    if (formId) {
      loadForm();
    }
  }, [formId]);

  const loadForm = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/management-forms/${formId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      let formData = response.data.data;
      
      // Replace placeholders with actual practice data
      if (practice) {
        let content = formData.overview || '';
        
        // Format address properly
        let practiceAddress = '';
        if (practice.address) {
          if (typeof practice.address === 'object') {
            const addr = practice.address;
            practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
          } else {
            practiceAddress = practice.address;
          }
        } else if (practice.location) {
          practiceAddress = practice.location;
        }
        
        content = content.replace(/{PracticeName}/g, practice.name || 'Dental Practice');
        content = content.replace(/{PracticeAddress}/g, practiceAddress);
        content = content.replace(/{PracticePhone}/g, practice.phone || '');
        content = content.replace(/{PracticeEmail}/g, practice.email || '');
        formData = { ...formData, overview: content };
      }
      
      setForm(formData);
      setEditContent(formData.overview || '');
    } catch (err) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to load form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/management-forms/${formId}/overview`,
        { overview: editContent },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setForm({ ...form, overview: editContent });
      setIsEditing(false);
      
      toast({
        title: "Success",
        description: "Form updated successfully",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update form. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast({
        title: "Generating PDF...",
        description: "Please wait while we create your form.",
        variant: "default",
      });
      
      // Fetch practice data if not available
      let practiceData = practice;
      if (!practice || !practice.name) {
        try {
          const practiceApi = (await import('../services/authApi')).practiceApi;
          const dashboardData = await practiceApi.getDashboard();
          
          if (dashboardData.success && dashboardData.data?.practice) {
            practiceData = dashboardData.data.practice;
          }
        } catch (error) {
          console.error('Failed to fetch practice data:', error);
        }
      }
      
      const success = await generateManagementFormPDF(form, practiceData);
      
      if (success) {
        toast({
          title: "PDF Downloaded",
          description: "Your form has been downloaded successfully.",
          variant: "default",
        });
      } else {
        toast({
          title: "Download Failed",
          description: "Failed to generate PDF. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: "Download Error",
        description: "An error occurred while generating the PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePrint = () => {
    // Open print-friendly version in new window
    const printWindow = window.open('', '', 'height=800,width=800');
    const practiceName = practice?.name || 'Dental Practice';
    
    // Format address properly
    let practiceAddress = '';
    if (practice?.address) {
      if (typeof practice.address === 'object') {
        const addr = practice.address;
        practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      } else {
        practiceAddress = practice.address;
      }
    } else if (practice?.location) {
      practiceAddress = practice.location;
    }
    
    const practicePhone = practice?.phone || '';
    const practiceEmail = practice?.email || '';
    const practiceLogo = practice?.branding?.logo || '';
    
    printWindow.document.write('<html><head><title>' + form.title + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('@page { margin: 0.75in; }');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; margin: 0; }');
    printWindow.document.write('.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }');
    printWindow.document.write('.logo { max-width: 150px; max-height: 80px; margin: 0 auto 15px; display: block; }');
    printWindow.document.write('.practice-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }');
    printWindow.document.write('.practice-info { font-size: 12px; color: #666; }');
    printWindow.document.write('.form-title { font-size: 18px; font-weight: bold; margin: 20px 0 15px 0; background: #2563eb; color: white; padding: 10px 15px; border-radius: 4px; }');
    printWindow.document.write('.checkbox { display: inline-block; width: 15px; height: 15px; border: 1px solid #333; margin: 0 8px; vertical-align: middle; }');
    printWindow.document.write('pre { white-space: pre-wrap; font-family: Arial; margin-bottom: 60px; page-break-inside: avoid; }');
    printWindow.document.write('.footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666; page-break-inside: avoid; }');
    printWindow.document.write('.footer-title { font-weight: bold; font-size: 12px; margin-bottom: 5px; }');
    printWindow.document.write('@media print { body { padding: 0; } @page { margin: 0.75in; } .footer { position: running(footer); } }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    
    // Header with logo and practice info
    printWindow.document.write('<div class="header">');
    if (practiceLogo) {
      const logoSrc = practiceLogo.startsWith('data:image') ? practiceLogo : `data:image/png;base64,${practiceLogo}`;
      printWindow.document.write('<img src="' + logoSrc + '" class="logo" alt="Practice Logo" />');
    }
    printWindow.document.write('<div class="practice-name">' + practiceName + '</div>');
    if (practiceAddress) printWindow.document.write('<div class="practice-info">' + practiceAddress + '</div>');
    if (practicePhone) printWindow.document.write('<div class="practice-info">Phone: ' + practicePhone + '</div>');
    if (practiceEmail) printWindow.document.write('<div class="practice-info">Email: ' + practiceEmail + '</div>');
    printWindow.document.write('</div>');
    
    // Form content (NO category)
    printWindow.document.write('<div class="form-title">' + form.title + '</div>');
    
    // Replace placeholders and process content with checkboxes
    let content = form.overview || '';
    content = content.replace(/{PracticeName}/g, practiceName);
    content = content.replace(/{PracticeAddress}/g, practiceAddress);
    content = content.replace(/{PracticePhone}/g, practicePhone);
    content = content.replace(/{PracticeEmail}/g, practiceEmail);
    
    const lines = content.split('\n');
    let processedContent = '';
    
    for (let line of lines) {
      const trimmedLine = line.trim();
      // Replace ■ (BLACK SQUARE) with HTML checkbox
      if (trimmedLine.includes('■')) {
        const replaced = trimmedLine.replace(/■/g, '<span class="checkbox"></span>');
        processedContent += replaced + '\n';
      }
      // Replace [ ] markers with HTML checkbox
      else if (trimmedLine.match(/^\[\s*\]/)) {
        const textAfterCheckbox = trimmedLine.replace(/^\[\s*\]\s*/, '');
        processedContent += '<span class="checkbox"></span>' + textAfterCheckbox + '\n';
      } else {
        processedContent += line + '\n';
      }
    }
    
    printWindow.document.write('<pre>' + processedContent + '</pre>');
    
    // Footer with PractCom and copyright
    printWindow.document.write('<div class="footer">');
    printWindow.document.write('<div class="footer-title">PractCom</div>');
    printWindow.document.write('<div>© ' + new Date().getFullYear() + ' The Oncall Bot LLC. All rights reserved.</div>');
    printWindow.document.write('<div>Powered by PractCom</div>');
    printWindow.document.write('</div>');
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    // Chrome no longer reliably fires onload from a document.write'd script.
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires; guard prevents the ghost-window flash.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Header 
          title="Loading..."
          subtitle="Loading form..."
          showBackButton={true}
          onBackClick={onBack}
          showBranding={false}
        />
        
        <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-32">
            <LoadingSpinner size="xl" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="flex items-center mb-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </Button>
          </div>
          <ErrorMessage 
            message={error || "Form not found"} 
            onRetry={loadForm}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center flex-1">
              <Button 
                variant="ghost" 
                onClick={onBack}
                className="mr-4 p-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1">
                <Badge variant="secondary" className="mb-2">
                  {form.category}
                </Badge>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {form.title}
                </h1>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              {!isEditing && (
                <>
                  <Button 
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button 
                    onClick={handlePrint}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                  <Button 
                    onClick={handleDownloadPDF}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </>
              )}
              {isEditing && (
                <>
                  <Button 
                    onClick={() => {
                      setIsEditing(false);
                      setEditContent(form.overview);
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveEdit}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <Card className="mb-8 border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            {!isEditing ? (
              <div className="text-gray-700 leading-relaxed">
                {form.overview.split('\n').map((line, index) => {
                  const trimmedLine = line.trim();
                  
                  // Handle empty lines
                  if (trimmedLine === '') {
                    return <div key={index} className="mb-2"></div>;
                  }
                  
                  // Handle checkboxes - ■ (BLACK SQUARE)
                  if (trimmedLine.includes('■')) {
                    const parts = trimmedLine.split('■');
                    return (
                      <div key={index} className="flex items-center flex-wrap mb-2">
                        {parts.map((part, partIndex) => (
                          <React.Fragment key={partIndex}>
                            {partIndex > 0 && (
                              <div className="w-4 h-4 border-2 border-gray-400 rounded mr-2 ml-2 flex-shrink-0"></div>
                            )}
                            {part.trim() && (
                              <span className="text-gray-700">{part.trim()}</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  }
                  
                  // Handle checkboxes - [  ] or [ ] (fallback)
                  const checkboxMatch = trimmedLine.match(/^\[\s*\]\s*(.*)/);
                  if (checkboxMatch) {
                    const textAfterCheckbox = checkboxMatch[1];
                    return (
                      <div key={index} className="flex items-start mb-2">
                        <div className="w-4 h-4 border-2 border-gray-400 rounded mr-3 mt-1 flex-shrink-0"></div>
                        <span className="flex-1 text-gray-700">{textAfterCheckbox}</span>
                      </div>
                    );
                  }
                  
                  // Handle bullet points
                  if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
                    const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
                    return (
                      <div key={index} className="flex items-start mb-2 ml-4">
                        <span className="text-blue-600 mr-3 text-lg leading-none">•</span>
                        <span className="flex-1 text-gray-700">{bulletText}</span>
                      </div>
                    );
                  }
                  
                  // Handle markdown-style headers
                  if (trimmedLine.includes('**')) {
                    if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && trimmedLine.length > 4) {
                      const headerText = trimmedLine.replace(/\*\*/g, '');
                      return (
                        <h4 key={index} className="font-bold text-gray-900 mt-6 mb-3 text-lg">
                          {headerText}
                        </h4>
                      );
                    }
                    else {
                      const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/);
                      return (
                        <p key={index} className="mb-3">
                          {parts.map((part, partIndex) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return (
                                <strong key={partIndex} className="font-semibold text-gray-900">
                                  {part.replace(/\*\*/g, '')}
                                </strong>
                              );
                            }
                            return part;
                          })}
                        </p>
                      );
                    }
                  }
                  
                  // Regular paragraphs
                  if (trimmedLine.length > 0) {
                    return (
                      <p key={index} className="mb-3 text-gray-700 leading-relaxed">
                        {trimmedLine}
                      </p>
                    );
                  }
                  
                  return null;
                }).filter(Boolean)}
              </div>
            ) : (
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-96 p-4 border rounded-md font-mono text-sm"
                placeholder="Enter form content..."
              />
            )}
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mt-8 bg-gray-50 border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Phone className="h-5 w-5 mr-2 text-gray-600" />
              Need Help?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              If you have questions about this form or need assistance:
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-4 bg-white rounded-lg border">
                <h4 className="font-semibold text-gray-800 mb-2">Office Hours</h4>
                <p className="text-gray-600 text-sm">Contact your dental office during regular business hours</p>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <h4 className="font-semibold text-gray-800 mb-2">After Hours</h4>
                <p className="text-gray-600 text-sm">Follow your dentist's emergency contact instructions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagementFormDetailPage;
